import { IconName } from "@zenchef/icomoon";
import React, { useEffect, useState, createContext, useContext } from "react";
import { Menu, MenuItemProps, Portal, useMenu } from "@ark-ui/react";
import Icon from "../Icon/Icon";
import { HStack } from "@zenchef/styled-system/jsx";
import { aromaNavMenu } from "@zenchef/styled-system/recipes";
import { token } from "@zenchef/styled-system/tokens";
import { useWindowSize } from "../../utils";

type ArkUiPlacement = NonNullable<Menu.RootProps["positioning"]>["placement"];

const MapAromaPlacementArkPlacement: Record<string, ArkUiPlacement> = {
  top: "right-start",
  bottom: "right-end",
};

interface MenuItem {
  value: string;
  disabled?: boolean;
  icon?: IconName;
  label?: string;
  trailing?: React.ReactNode;
  colorTheme?: string;
  isSecondary?: boolean;
}

export type NavMenuProps = Menu.RootProps & {
  trigger: React.ReactNode;
  children?: React.ReactNode;
  asSubmenu?: boolean;
  placement?: keyof typeof MapAromaPlacementArkPlacement;
  zIndex?: string;
  positionerClass?: string;
  isSecondary?: boolean;
};

type NavMenuItemGroupProps = Menu.ItemGroupProps & {
  label?: string;
};
type NavMenuItemProps = MenuItem & MenuItemProps;

const NavMenuContext = createContext<{ isSecondary?: boolean }>({});

const NavMenuItemGroup = ({
  children,
  label,
  ...props
}: NavMenuItemGroupProps) => {
  return (
    <Menu.ItemGroup {...props}>
      {label ? <Menu.ItemGroupLabel>{label}</Menu.ItemGroupLabel> : null}
      {children}
    </Menu.ItemGroup>
  );
};

const NavMenuItem = ({
  icon,
  label,
  trailing,
  colorTheme,
  isSecondary,
  ...props
}: NavMenuItemProps) => {
  const context = useContext(NavMenuContext);
  const isItemSecondary = isSecondary ?? context.isSecondary;

  return (
    <Menu.Item {...props}>
      <HStack gap="gap.2" color={colorTheme}>
        {icon ? (
          <Icon
            name={icon}
            fontSize="125"
            data-part={isItemSecondary ? "icon-secondary" : "icon-primary"}
          />
        ) : null}
        {label}
      </HStack>
      {trailing ? (
        <div onClick={(e) => e.stopPropagation()}>{trailing}</div>
      ) : null}
    </Menu.Item>
  );
};

const NavMenu = ({
  trigger,
  children,
  placement,
  positioning,
  zIndex,
  asSubmenu = false,
  positionerClass,
  open,
  isSecondary,
  ...props
}: NavMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const { width: windowWidth } = useWindowSize();
  const isMobileScreen = windowWidth <= parseInt(token("breakpoints.sm"), 10);

  const isTabletDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /ipad|android(?!.*mobile)|tablet|playbook/.test(userAgent);
  };

  const computedPositioning: Menu.RootProps["positioning"] = placement
    ? {
        placement: isMobileScreen
          ? "bottom"
          : MapAromaPlacementArkPlacement[placement],
        offset: {
          mainAxis: isMobileScreen ? 8 : 0,
          crossAxis: placement === "top" ? -16 : 16,
        },
        ...positioning,
      }
    : positioning;

  const TriggerTag = asSubmenu ? Menu.TriggerItem : Menu.Trigger;

  useEffect(() => {
    if (isOpen || visible || open) {
      menu.api.setOpen(true);
    }
    if (!isOpen && !visible && !open) {
      menu.api.setOpen(false);
    }
  }, [isOpen, visible]);

  const menu = useMenu();

  return (
    <NavMenuContext.Provider value={{ isSecondary }}>
      <Menu.Root
        positioning={computedPositioning}
        {...props}
        open={menu.api.open}
        onSelect={() => menu.api.setOpen(false)}
        onEscapeKeyDown={() => menu.api.setOpen(false)}
        onFocusOutside={() => menu.api.setOpen(false)}
        onInteractOutside={() => menu.api.setOpen(false)}
      >
        <TriggerTag
          asChild
          onClick={() => menu.api.setOpen(!menu.api.open)}
          onPointerEnter={() => !isMobileScreen && setIsOpen(true)}
          onPointerLeave={() => !isTabletDevice() && setIsOpen(false)}
        >
          {trigger}
        </TriggerTag>
        <Portal>
          <Menu.Positioner
            style={{ zIndex }}
            className={positionerClass}
            onPointerEnter={() => setVisible(true)}
            onPointerLeave={() => !isTabletDevice() && setVisible(false)}
          >
            <div className={aromaNavMenu()}>
              <Menu.Content>{children}</Menu.Content>
            </div>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </NavMenuContext.Provider>
  );
};

const NavMenuWrapper = ({ children }: { children: React.ReactNode }) => {
  const menu = useMenu();

  return (
    <Menu.RootProvider value={menu}>
      <Menu.Root>
        <Menu.Trigger></Menu.Trigger>
        {children}
      </Menu.Root>
    </Menu.RootProvider>
  );
};

NavMenu.Item = NavMenuItem;
NavMenu.ItemGroup = NavMenuItemGroup;
NavMenu.Wrapper = NavMenuWrapper;

export default NavMenu;
